import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { FileTextIcon, MenuIcon, UserIcon, StarIcon, LogOutIcon, BellIcon } from './Icons';
import UpgradeModal from './UpgradeModal';
import NotificationDropdown from './NotificationDropdown';
import api, { notificationAPI, documentAPI, InAppNotification } from '../services/api';
import {
  showBrowserNotification,
  getBrowserNotificationPermission,
  requestBrowserNotificationPermission
} from '../utils/browserNotification';

// Polling intervals
const NORMAL_POLL_INTERVAL = 30000; // 30 seconds
const FAST_POLL_INTERVAL = 5000; // 5 seconds after test notification

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation('common');
  const { getLocalizedPath } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [badgeText, setBadgeText] = useState<string>('');
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [pollInterval, setPollInterval] = useState(NORMAL_POLL_INTERVAL);
  const [hasDocuments, setHasDocuments] = useState<boolean | null>(() => {
    const usage = user?.subscription_usage;
    if (usage && typeof usage.contracts_this_month === 'number') {
      return usage.contracts_this_month > 0;
    }
    return null;
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const lastKnownNotificationIdRef = useRef<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHasDocuments = async () => {
      if (!user) return;
      try {
        const res = await documentAPI.list();
        const total = (res.data as any)?.total;
        const docs = (res.data as any)?.documents || [];
        const count = typeof total === 'number' ? total : docs.length;
        setHasDocuments(count > 0);
      } catch (error) {
        console.error('Failed to check documents:', error);
        setHasDocuments((prev) => prev ?? null);
      }
    };

    fetchHasDocuments();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    let objectUrl: string | null = null;

    const loadProfilePhoto = async () => {
      if (!user?.profile_photo_url) {
        setProfilePhotoUrl(null);
        return;
      }

      try {
        const response = await api.get(user.profile_photo_url, {
          responseType: 'blob',
        });

        objectUrl = URL.createObjectURL(response.data);
        setProfilePhotoUrl(objectUrl);
      } catch (error) {
        console.error('Failed to load profile photo:', error);
        setProfilePhotoUrl(null);
      }
    };

    loadProfilePhoto();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [user?.profile_photo_url]);

  // Fetch unread notification count and check for new notifications
  const fetchUnreadCount = useCallback(async (showBrowserNotif: boolean = true) => {
    if (!user) return;

    try {
      // Fetch both count and latest notifications
      const [countResponse, listResponse] = await Promise.all([
        notificationAPI.getUnreadCount(),
        notificationAPI.list(true, 5, 0) // Get latest 5 unread
      ]);

      setUnreadCount(countResponse.data.unread_count);
      setBadgeText(countResponse.data.badge_text);

      // Check for new notifications and show browser notification
      const latestNotifications = listResponse.data.notifications;
      if (latestNotifications.length > 0) {
        const latestId = latestNotifications[0].id;
        const previousLastId = lastKnownNotificationIdRef.current;

        // If we have a new notification (higher ID than last known) AND we should show browser notifications
        if (showBrowserNotif && previousLastId !== null && latestId > previousLastId) {
          // Find all new notifications
          const newNotifications = latestNotifications.filter(
            (n: InAppNotification) => n.id > previousLastId
          );

          console.log('[Navbar] New notifications detected:', newNotifications.length);

          // Show browser notification for each new one (max 3 to avoid spam)
          const permission = getBrowserNotificationPermission();
          console.log('[Navbar] Browser notification permission:', permission);

          if (permission === 'granted') {
            newNotifications.slice(0, 3).forEach((notification: InAppNotification) => {
              console.log('[Navbar] Showing browser notification:', notification.title);
              const result = showBrowserNotification(notification.title, {
                body: notification.body,
                tag: `notification-${notification.id}`,
                onClick: () => {
                  if (notification.deep_link) {
                    navigate(notification.deep_link);
                  }
                }
              });
              console.log('[Navbar] Browser notification result:', result);
            });
          } else if (permission === 'default') {
            // Permission not yet requested - request it
            console.log('[Navbar] Requesting browser notification permission...');
            requestBrowserNotificationPermission().then((newPermission) => {
              console.log('[Navbar] Permission result:', newPermission);
            });
          }
        }

        // Always update last known ID (even on initial fetch to set baseline)
        lastKnownNotificationIdRef.current = latestId;
      } else if (lastKnownNotificationIdRef.current === null) {
        // Initialize with 0 if no notifications exist
        lastKnownNotificationIdRef.current = 0;
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [user, navigate]);

  useEffect(() => {
    // Initial fetch without browser notification (to set baseline)
    fetchUnreadCount(false);

    // Poll with configured interval
    const interval = setInterval(() => fetchUnreadCount(true), pollInterval);

    return () => clearInterval(interval);
  }, [user, pollInterval, fetchUnreadCount]);

  // Listen for test notification events to switch to fast polling
  useEffect(() => {
    const handleTestNotificationSent = () => {
      console.log('[Navbar] Test notification sent event received, switching to fast polling');
      // Switch to fast polling for 30 seconds
      setPollInterval(FAST_POLL_INTERVAL);
      // Immediately fetch to catch the notification quickly
      fetchUnreadCount(true);

      // Reset to normal polling after 30 seconds
      const timeout = setTimeout(() => {
        console.log('[Navbar] Resetting to normal polling interval');
        setPollInterval(NORMAL_POLL_INTERVAL);
      }, 30000);

      return () => clearTimeout(timeout);
    };

    window.addEventListener('testNotificationSent', handleTestNotificationSent);
    return () => window.removeEventListener('testNotificationSent', handleTestNotificationSent);
  }, [fetchUnreadCount]);

  if (!user) return null;

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
  };

  const handleNotificationDropdownClose = () => {
    setShowNotificationDropdown(false);
    // Refresh unread count after dropdown closes
    fetchUnreadCount();
  };

  return (
    <nav className={`navbar ${isMenuOpen ? 'navbar--menu-open' : ''} ${showNotificationDropdown ? 'navbar--notif-open' : ''}`}>
      <Link
        to={getLocalizedPath(hasDocuments ? '/catalog' : '/upload')}
        className="navbar-brand"
      >
        <img src="/logo_icon_only.png" alt="Contrato Seguro" className="navbar-logo" />
        <span>Contrato Seguro</span>
      </Link>
      <div className="navbar-actions">
        <div className="navbar-notification-container" ref={notificationRef}>
          <button
            className="btn navbar-btn navbar-btn-notifications"
            onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
          >
            <div className="navbar-notification-icon">
              <BellIcon size={18} />
              {unreadCount > 0 && (
                <span className="navbar-notification-badge">{badgeText}</span>
              )}
            </div>
          </button>
          <NotificationDropdown
            isOpen={showNotificationDropdown}
            onClose={handleNotificationDropdownClose}
          />
        </div>

        <div className="navbar-menu-container" ref={menuRef}>
          <button
            className="btn btn-secondary navbar-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <MenuIcon size={20} />
            <span>{t('nav.menu', { defaultValue: t('nav.myAccount') })}</span>
          </button>

          {isMenuOpen && (
            <div className="navbar-dropdown">
              <div className="navbar-dropdown-header">
                <div className="navbar-dropdown-avatar">
                  {profilePhotoUrl ? (
                    <img src={profilePhotoUrl} alt="Profile" className="navbar-dropdown-avatar-img" />
                  ) : (
                    <UserIcon size={20} />
                  )}
                </div>
                <div className="navbar-dropdown-user">
                  <div className="navbar-dropdown-name">{user.full_name || t('nav.myAccount')}</div>
                  <div className="navbar-dropdown-email">{user.email}</div>
                </div>
              </div>

              <div className="navbar-dropdown-divider" />

              <button
                className="navbar-dropdown-item"
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate(getLocalizedPath('/catalog'));
                }}
              >
                <FileTextIcon size={18} />
                <span>{t('nav.catalog')}</span>
              </button>

              <button
                className="navbar-dropdown-item"
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate(getLocalizedPath('/profile'));
                }}
              >
                <UserIcon size={18} />
                <span>{t('nav.profile')}</span>
              </button>

              <button
                className="navbar-dropdown-item navbar-dropdown-upgrade"
                onClick={() => {
                  setIsMenuOpen(false);
                  setShowUpgradeModal(true);
                }}
              >
                <StarIcon size={18} />
                <span>{t('subscription.manage', { defaultValue: 'Manage subscription' })}</span>
              </button>

              <div className="navbar-dropdown-divider" />

              <button
                className="navbar-dropdown-item navbar-dropdown-logout"
                onClick={handleLogout}
              >
                <LogOutIcon size={18} />
                <span>{t('nav.logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {showUpgradeModal && ReactDOM.createPortal(
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />,
        document.body
      )}
    </nav>
  );
};

export default Navbar;
